import { apiRequest } from '../utils/api';

/**
 * Which buildings' "New" markers the viewer has already clicked, driving the
 * greyed `new-seen` marker variant.
 *
 * Signed-in viewers get the server's set (shared across devices, expired by the
 * backend's TTL index). Anonymous viewers keep the original browser-local set,
 * which is the right scope for them anyway — there is no account to hang it on.
 */

const SEEN_NEW_LISTINGS_KEY = 'jaa90:seen-new-listings';

// Null until a bounds response says otherwise, and null again whenever one
// reports an anonymous viewer. Non-null means "use the server's set".
let serverSeen: Set<string> | null = null;

const loadLocalSeen = (): Set<string> => {
  try {
    const raw = localStorage.getItem(SEEN_NEW_LISTINGS_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr.map(String) : []);
  } catch {
    return new Set();
  }
};

const markLocalSeen = (buildingId: string): void => {
  const set = loadLocalSeen();
  if (set.has(buildingId)) return;
  set.add(buildingId);
  try {
    localStorage.setItem(SEEN_NEW_LISTINGS_KEY, JSON.stringify(Array.from(set)));
  } catch {
    // ignore quota errors
  }
};

/**
 * Adopt the `seenBuildingIds` from an /off-market-listings-in-bounds response.
 * The backend sends null for anonymous callers and an array (possibly empty)
 * for signed-in ones, so this can't confuse "not signed in" with "nothing
 * clicked yet".
 */
export const setServerSeenBuildings = (ids: unknown): void => {
  serverSeen = Array.isArray(ids) ? new Set(ids.map(String)) : null;
};

/** The active seen set — server-backed when signed in, browser-local otherwise. */
export const loadSeenNewBuildings = (): Set<string> =>
  serverSeen ?? loadLocalSeen();

/**
 * Record a building as seen. Updates the in-memory set first so the marker
 * greys out on this repaint rather than waiting for the next bounds fetch; the
 * POST is fire-and-forget because a lost write only costs a still-yellow
 * marker, and the next click retries it.
 */
export const markBuildingSeen = (buildingId: string): void => {
  if (!serverSeen) {
    markLocalSeen(buildingId);
    return;
  }
  if (serverSeen.has(buildingId)) return;
  serverSeen.add(buildingId);
  apiRequest(`/buildings/${encodeURIComponent(buildingId)}/seen`, {
    method: 'POST',
  }).catch(() => {
    // Drop the optimistic entry so a later fetch or click can try again.
    serverSeen?.delete(buildingId);
  });
};
